import type { ComponentPropsWithoutRef, Ref } from 'react';
import {
  Expand,
  FileSpreadsheet,
  FileUser,
  FlaskConical,
  Headset,
  Image,
  Languages,
  ListChecks,
  LoaderPinwheel,
  Mail,
  NotebookPen,
  Siren,
  Lightbulb,
  Type,
  Users,
  Video,
  Waypoints,
  Workflow,
  Wrench,
  ChartColumnIncreasing,
  ClapperboardIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { CATEGORY_COLORS, type CategoryId } from '@/shared/constants/category-colors';

import { DEFAULT_ICON_SIZE } from './GeneralIcons.constants';
import type { IconConfig, SpringBoardIconProps } from './GeneralIcons.types';
import { generalIconV4Variants, type GeneralIconV4VariantProps } from './GeneralIcons.v4.variants';

const PATHS: Record<string, IconConfig> = {
  DefaultToolIcon: {
    name: 'DefaultToolIcon',
    path: 'M9.50175 19C8.18775 19 6.95267 18.7507 5.7965 18.252C4.64033 17.7533 3.63467 17.0766 2.7795 16.2218C1.92433 15.3669 1.24725 14.3617 0.74825 13.206C0.249417 12.0503 0 10.8156 0 9.50175C0 8.18775 0.249417 6.95267 0.74825 5.7965C1.24692 4.64033 1.92375 3.63467 2.77875 2.7795C3.63375 1.92433 4.63917 1.24725 5.795 0.74825C6.95083 0.249417 8.18583 0 9.5 0H10.25V8.20775C10.4858 8.35258 10.6698 8.53325 10.802 8.74975C10.934 8.96625 11 9.21675 11 9.50125C11 9.90942 10.8523 10.2613 10.5568 10.5568C10.2613 10.8523 9.909 11 9.5 11C9.091 11 8.73875 10.8523 8.44325 10.5568C8.14775 10.2613 8 9.90975 8 9.50225C8 9.21825 8.066 8.96517 8.198 8.743C8.33017 8.52083 8.51417 8.34242 8.75 8.20775V5.5865C7.81917 5.76217 7.04483 6.21375 6.427 6.94125C5.809 7.66892 5.5 8.52183 5.5 9.5C5.5 10.6 5.89167 11.5417 6.675 12.325C7.45833 13.1083 8.4 13.5 9.5 13.5C10.6 13.5 11.5417 13.1083 12.325 12.325C13.1083 11.5417 13.5 10.6 13.5 9.5C13.5 8.9 13.3792 8.34583 13.1375 7.8375C12.8958 7.32917 12.5667 6.88333 12.15 6.5L13.2193 5.43075C13.7641 5.93592 14.1971 6.53725 14.5182 7.23475C14.8394 7.93242 15 8.6875 15 9.5C15 11.0278 14.4655 12.3264 13.3965 13.3957C12.3275 14.4652 11.0294 15 9.50225 15C7.97508 15 6.67625 14.4652 5.60575 13.3957C4.53525 12.3264 4 11.0278 4 9.5C4 8.09617 4.45417 6.88175 5.3625 5.85675C6.27083 4.83175 7.4 4.23142 8.75 4.05575V1.53075C6.709 1.71658 4.99042 2.56758 3.59425 4.08375C2.19808 5.59975 1.5 7.40517 1.5 9.5C1.5 11.7333 2.275 13.625 3.825 15.175C5.375 16.725 7.26667 17.5 9.5 17.5C11.7333 17.5 13.625 16.725 15.175 15.175C16.725 13.625 17.5 11.7333 17.5 9.5C17.5 8.35 17.275 7.275 16.825 6.275C16.375 5.275 15.7583 4.40833 14.975 3.675L16.0443 2.60575C16.9557 3.47758 17.6763 4.5035 18.2057 5.6835C18.7352 6.86367 19 8.13492 19 9.49725C19 10.8119 18.7507 12.0473 18.252 13.2035C17.7533 14.3597 17.0766 15.3653 16.2218 16.2205C15.3669 17.0757 14.3617 17.7528 13.206 18.2518C12.0503 18.7506 10.8156 19 9.50175 19Z',
    viewBox: '0 0 19 19',
  },
};

export interface GeneralIconV4Props
  extends
    Omit<ComponentPropsWithoutRef<'svg'>, 'color'>,
    SpringBoardIconProps,
    GeneralIconV4VariantProps {
  iconKey?: keyof typeof PATHS;
  /** React 19 ref-as-prop: forwarded to the underlying `<svg>`. */
  ref?: Ref<SVGSVGElement>;
}

export function GeneralIconV4({
  iconKey = 'DefaultToolIcon',
  color,
  size,
  className,
  ref,
  ...rest
}: GeneralIconV4Props) {
  const cfg = PATHS[iconKey] ?? PATHS.DefaultToolIcon;
  const iconSize = size || DEFAULT_ICON_SIZE;
  return (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={cfg.viewBox}
      width={iconSize}
      height={iconSize}
      fill={color ?? 'currentColor'}
      aria-hidden
      data-slot="general-icon"
      data-icon={cfg.name}
      className={generalIconV4Variants({ className })}
      {...rest}
    >
      <path d={cfg.path} />
    </svg>
  );
}

GeneralIconV4.displayName = 'GeneralIconV4';

const LUCIDE_ICONS: Record<string, LucideIcon> = {
  'audience-flow-agent': Users,
  'strategy-agent': Waypoints,
  'strategy-agent-v2': Waypoints,
  'strategy-agent-v3': Waypoints,
  'flow-digest-agent': Workflow,
  'opportunities-room': Siren,
  'external-tools-agent': Wrench,
  'content-test-agent': FlaskConical,
  'content-test': FlaskConical,
  'focus-group-agent': FileUser,
  'copy-generation': Type,
  'email-flow-agent': Mail,
  templates: Mail,
  ideation: Lightbulb,
  'image-generation': Image,
  'video-generation': Video,
  'content-studio': LoaderPinwheel,
  'creative-flows': Workflow,
  'reels-creator': ClapperboardIcon,
  'translation-agent': Languages,
  'localization-agent': NotebookPen,
  'adaptation-agent': FileSpreadsheet,
  Adaptation: Expand,
  'quality-assurance': ListChecks,
  'monksflow-sales-support': Headset,
  'briefing-agent': FileUser,
  'social-media-brief': FileUser,
  'general-support': Headset,
  'training-videos': Video,
  iq: ChartColumnIncreasing,
};

export const ToolIconsV4 = (
  agentId: string,
  tag: string,
  size: number,
  className?: string,
  colorOverride?: string,
): React.ReactElement => {
  const normalizedTag = tag.toLowerCase() || '';
  const isValidTag = (key: string): key is CategoryId => key in CATEGORY_COLORS;

  const categoryColors = isValidTag(normalizedTag)
    ? CATEGORY_COLORS[normalizedTag]
    : CATEGORY_COLORS.deliver;
  const color =
    colorOverride ??
    ('icon' in categoryColors ? (categoryColors as { icon: string }).icon : categoryColors.text);

  // First, try exact match
  let LucideIcon = LUCIDE_ICONS[agentId] as LucideIcon | undefined;

  // If no exact match, check for prefix matches (e.g., audience-flow-persona-1, audience-flow-persona-2)
  if (!LucideIcon) {
    if (agentId && agentId.startsWith('audience-flow')) {
      LucideIcon = LUCIDE_ICONS['audience-flow-agent'];
    }
  }

  if (LucideIcon) {
    return <LucideIcon size={size} color={color} className={className} />;
  }

  return <GeneralIconV4 color={color} size={size} className={className} />;
};
